import { HttpClient, HttpContext } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  signal,
  ValueProvider,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IPortalNotice } from '@inshuwa-client/models';
import { catchError, of, switchMap, timer } from 'rxjs';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import { SKIP_ERROR_TOAST } from '../interceptors/ic-http-interceptor.interceptor';
import { resolveTenantDomain } from '../utils/tenant-domain.utils';

/** Which portal this build is, so an announcement can be aimed at some of them. */
export const PORTAL_NAME = new InjectionToken<string>('PORTAL_NAME');

export function providePortalName(name: string): ValueProvider {
  return { provide: PORTAL_NAME, useValue: name };
}

const DISMISSED_KEY = 'ic-dismissed-notices';

@Injectable({ providedIn: 'root' })
export class NoticeService {
  private readonly http = inject(HttpClient);
  private readonly portal = inject(PORTAL_NAME, { optional: true });
  private readonly dismissed = signal(readDismissed());

  /**
   * Polled rather than pushed, and anonymous, so it also runs on the login page - which is where
   * someone most needs to hear that the thing they are about to use is down for maintenance.
   *
   * The tenant travels as the portal's own domain, resolved the same way the branding bootstrap
   * resolves it, because that is the only tenant signal a logged-out browser has: the API is on its
   * own hostname, so the server cannot read ours, and a build-time insurer id would have to be set
   * in every tenant's environment file. The server scopes the integration half by the token instead.
   */
  private readonly notices = toSignal(
    timer(0, 60_000).pipe(
      switchMap(() =>
        this.http
          .get<IPortalNotice[]>(`${baseUrl()}/notices`, {
            params: {
              ...(this.portal ? { portal: this.portal } : {}),
              domain: resolveTenantDomain(
                location?.search ?? '',
                location?.hostname ?? '',
              ),
            },
            // Nothing the user can act on, and it would otherwise toast once a minute, forever,
            // against any API that doesn't serve this endpoint yet.
            context: new HttpContext().set(SKIP_ERROR_TOAST, true),
          })
          .pipe(catchError(() => of<IPortalNotice[]>([]))),
      ),
    ),
    { initialValue: [] as IPortalNotice[] },
  );

  readonly visible = computed(() =>
    this.notices().filter((n) => !this.dismissed().includes(n.key)),
  );

  dismiss(notice: IPortalNotice): void {
    // Only keys still in the feed are kept, so the list prunes itself as announcements expire or
    // are reworded rather than growing forever in someone's browser. A failed poll serves an empty
    // feed, which is not the same thing as nothing being on air, so nothing is pruned against it.
    const live = this.notices().map((n) => n.key);
    const next = live.length
      ? [...this.dismissed(), notice.key].filter((key) => live.includes(key))
      : [...this.dismissed(), notice.key];

    this.dismissed.set(next);

    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private browsing or blocked storage: the dismissal lasts the session and that is fine.
    }
  }
}

function readDismissed(): string[] {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(DISMISSED_KEY) ?? '[]',
    );
    return Array.isArray(stored) ? stored.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
}
